/** Which marketing page is meant: the venue's own, or one track's. */
export interface MarketingPageChoice {
  eventTrackId: string | null
  label: string
}

/**
 * The marketing pages a venue should have, plus any it already has that it
 * shouldn't.
 *
 * **More than one track means a page per track**, and no venue-wide page: two
 * tracks are two different offerings — different seasons, capacities and prices
 * — and a venue-level page would have nothing to say that either track's did
 * not. One track is not that. It describes how the venue runs events, not what
 * it sells, so a single-track venue has one page like any other; all three
 * single-track venues in the data use the venue-wide page.
 *
 * Anything already stored outside that set is still offered, because the content
 * API goes on serving it and the editor is the only place that could change it.
 * Hiding a page the public can still read is the mistake this feature already
 * made once, with content that failed validation.
 *
 * Shared because the settings screen and the MCP tools offer the same pages: an
 * assistant that could start a venue-wide page for a venue with two tracks would
 * make a page the screen never offers.
 */
export function marketingPagesToOffer(
  venue: { tracks: readonly { id: string; name: string }[] },
  existing: readonly { eventTrackId: string | null }[],
): MarketingPageChoice[] {
  const perTrack = venue.tracks.length > 1
  const expected: MarketingPageChoice[] = perTrack
    ? venue.tracks.map((track) => ({ eventTrackId: track.id, label: track.name }))
    : [{ eventTrackId: null, label: 'Venue page' }]

  const strays = existing
    .filter((page) => !expected.some((choice) => choice.eventTrackId === page.eventTrackId))
    .map((page) => ({
      eventTrackId: page.eventTrackId,
      label:
        page.eventTrackId === null
          ? 'Venue page'
          : (venue.tracks.find((track) => track.id === page.eventTrackId)?.name ?? 'Other track'),
    }))

  return [...expected, ...strays]
}
